import React from 'react'
import cx from 'classnames'
import { ListItem, ListItemText, Grid, Box, Chip } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { LocalOffer } from '@mui/icons-material'
import { Link } from 'react-router-dom'
import { PatientTag } from 'src/graphql.generated'
import { CustomTooltip } from 'src/designSystem'
import { useSidebarControls } from 'src/designSystem/Container/sidebarControls'
import { CalendarDayIcon } from 'src/designSystem/icons'
import { useFeatureFlag } from 'src/featureFlags'
import MeruDate from 'src/legacy/lib/MeruDate'
import { isLocalDevelopment } from 'src/lib/env'
import { HomeViewParticipantFragment } from '../searchParticipants.generated'
import DraftMessageIndicator from './DraftMessageIndicator'
import HqOverdueIndicator from './HqOverdueIndicator'
import IndicatorTooltip from './IndicatorTooltip'
import MessageIndicator from './MessageIndicator'
import { MonthlySummaryIndicator } from './MonthlySummaryIndicator'
import PriorityIndicators from './PriorityIndicators'
import SuicidalityIndicator from './SuicidalityIndicator'
import { calendarDayLabel } from './calendarDayLabel'
import { shouldShowFollowUpIndicator } from './followUpTransitionUtils'
import { useDraftMessage } from './useDraftMessage'
import { shouldShowViewedTodayIndicator } from './viewedTodayUtils'

type Props = {
  uid: string
  active: boolean
  firstname?: string | null
  lastname?: string | null
  programID?: string | null
  language?: string | null
  source?: string | null
  indicators?: HomeViewParticipantFragment['indicators']
  tags?: Array<PatientTag> | null
  programStartDate?: Date | null
  showDayIndicator?: boolean
  timezone?: string | null
  enrolledFromPreviousAdvancedProgram?: boolean | null
  enrollmentCreatedAt?: Date | null
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
  },
  stacked: {
    flexDirection: 'column',
  },
  nameCell: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(0),
  },
  priorityRow: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    minWidth: 0,
  },
  expandedRow: {
    gap: theme.spacing(2),
  },
  collapsedRow: {
    gap: theme.spacing(0.25),
  },
  nameArea: {
    flex: '1 1 0',
    minWidth: theme.spacing(6),
    overflow: 'hidden',
  },
  followUpChip: {
    marginLeft: theme.spacing(1),
    height: theme.spacing(2.5),
  },
  indicatorCell: {
    display: 'flex',
    justifyContent: 'center',
  },
  leftmostGridItem: {
    display: 'flex',
  },
  priorityListItem: {
    backgroundColor: theme.palette.graySubtle,
    marginBottom: theme.spacing(0.25),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    // The theme paints this border on selected rows; in this row it marks "viewed today" only,
    // so the selected state has to give it up. The class is repeated to outweigh the theme's rule.
    '&&&': {
      borderRight: `${theme.spacing(0.5)} solid transparent`,
    },
  },
  viewedToday: {
    // One ampersand more than `priorityListItem` above, so this wins on specificity rather than
    // on the order the two rules happen to be declared in.
    '&&&&': {
      borderRight: `${theme.spacing(0.5)} solid ${theme.palette.primary.main}`,
    },
  },
  listItem: {
    '&.Mui-selected': {
      backgroundColor: theme.palette.selectedBackground,
      borderRight: `${theme.spacing(0.5)} solid ${theme.palette.primary.main}`,
    },
  },
}))

const Ellipsis: React.FC = (props) => <Box overflow="hidden" textOverflow="ellipsis" {...props} />

export default function ParticipantListItem(props: Props) {
  const {
    uid,
    active,
    firstname,
    lastname,
    programID,
    language,
    source,
    indicators,
    tags,
    programStartDate,
    showDayIndicator,
    timezone,
    enrolledFromPreviousAdvancedProgram,
    enrollmentCreatedAt,
  } = props

  const classes = useStyles()
  const hasPriorityFilters = useFeatureFlag('featureFlagDashboardPriorityFilters')
  const { isExpanded: isSidebarExpanded, shrink: shrinkSidebar } = useSidebarControls()
  const dayInWeekNbr = MeruDate.now(timezone).formatWeekDayNumber(programStartDate)
  const newMessagesCount = indicators?.newMessages?.count
  const isSuicidal = indicators?.suicidal
  const showMonthlySummary = indicators?.monthlySummaryNotes?.showIndicator ?? false
  const showFollowUpIndicator = shouldShowFollowUpIndicator(enrolledFromPreviousAdvancedProgram, enrollmentCreatedAt)
  const draftMessage = useDraftMessage(uid)
  const showViewedToday =
    hasPriorityFilters && shouldShowViewedTodayIndicator(indicators?.lastViewedAt, newMessagesCount, !!draftMessage)

  const tagIndicator = tags && tags.length > 0 && (
    <CustomTooltip placement="right-end" title={tags.map((tag) => tag.name).join(', ')}>
      <LocalOffer fontSize="small" titleAccess="participant tags" />
    </CustomTooltip>
  )

  // Developer aid for telling test patients apart; the list query only fetches language and source locally.
  const devInfo = isLocalDevelopment ? `${programID ?? '—'} · ${language ?? 'en'} · ${source ?? '—'}` : undefined

  const name = (
    <Box display="flex" alignItems="center">
      <ListItemText
        classes={{ root: cx(classes.root, { [classes.stacked]: devInfo }) }}
        primary={`${firstname} ${lastname}`}
        primaryTypographyProps={{
          component: Ellipsis,
        }}
        secondary={devInfo}
        secondaryTypographyProps={{
          variant: 'caption',
          component: Ellipsis,
        }}
      />
      {showFollowUpIndicator && (
        <CustomTooltip title="Recently transitioned from a prior stage (program) of Advanced.">
          <Chip className={classes.followUpChip} size="small" color="secondary" variant="outlined" label="Follow-up" />
        </CustomTooltip>
      )}
    </Box>
  )

  const monthlySummaryIndicator = showMonthlySummary && <MonthlySummaryIndicator />
  const suicidalityIndicator = isSuicidal && <SuicidalityIndicator />
  const dayIndicator = showDayIndicator && dayInWeekNbr != null && (
    <IndicatorTooltip title={calendarDayLabel(dayInWeekNbr)}>
      <span data-testid="day-indicator">
        <CalendarDayIcon day={dayInWeekNbr} />
      </span>
    </IndicatorTooltip>
  )
  const hqOverdueIndicator = indicators?.overdueHealthQuestionnaire && <HqOverdueIndicator />
  const messageIndicator = newMessagesCount !== undefined && <MessageIndicator count={newMessagesCount} />

  return (
    <ListItem
      button
      component={Link}
      selected={active}
      to={`/participant/${uid}`}
      // Container shrinks the list when the selection changes; this also shrinks it on reopening the selected participant.
      onClick={shrinkSidebar}
      classes={{
        root: cx({
          [classes.listItem]: !hasPriorityFilters,
          [classes.priorityListItem]: hasPriorityFilters,
          [classes.viewedToday]: showViewedToday,
        }),
      }}
    >
      {hasPriorityFilters ? (
        <Box className={cx(classes.priorityRow, isSidebarExpanded ? classes.expandedRow : classes.collapsedRow)}>
          <Box className={classes.nameArea}>{name}</Box>

          <PriorityIndicators
            expanded={isSidebarExpanded}
            draftMessage={draftMessage}
            indicators={indicators}
            tags={tags}
            programStartDate={programStartDate}
            showDayIndicator={showDayIndicator}
            timezone={timezone}
          />
        </Box>
      ) : (
        <Grid container alignItems="center">
          <Grid classes={{ root: classes.leftmostGridItem }} item xs={1}>
            {tagIndicator}
          </Grid>

          <Grid classes={{ root: classes.nameCell }} item xs={6}>
            {name}
          </Grid>

          <Grid classes={{ root: classes.indicatorCell }} item xs={1}>
            {monthlySummaryIndicator}
          </Grid>

          <Grid classes={{ root: classes.indicatorCell }} item xs={1}>
            {suicidalityIndicator}
          </Grid>

          <Grid classes={{ root: classes.indicatorCell }} item xs={1}>
            {dayIndicator}
          </Grid>

          <Grid classes={{ root: classes.indicatorCell }} item xs={1}>
            {hqOverdueIndicator}
          </Grid>

          <Grid classes={{ root: classes.indicatorCell }} item xs={1}>
            {messageIndicator}
          </Grid>

          <Grid classes={{ root: classes.indicatorCell }} item xs={1}>
            <DraftMessageIndicator draftMessage={draftMessage} />
          </Grid>
        </Grid>
      )}
    </ListItem>
  )
}
